import React from 'react';
import { cn } from '@/lib/utils';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import { LocalTransaction, formatAmountWithColor, formatTransactionDate } from '../utils/TransactionFormatters';
import { ColumnsRecord } from './TransactionTableHeader';

interface TransactionRowProps {
  transaction: LocalTransaction;
  columnOrder: string[];
  transactionColumns: ColumnsRecord;
  isSelected: boolean;
  accounts: any[];
  categories: any[];
  tags: any[];
  onToggleSelection: (transactionId: string) => void;
  onEdit: (transaction: LocalTransaction) => void;
  onDelete: (transactionId: string) => void;
}

export const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction,
  columnOrder,
  transactionColumns,
  isSelected,
  accounts,
  categories,
  tags,
  onToggleSelection,
  onEdit,
  onDelete
}) => {
  // Get the account to determine the currency
  const account = accounts.find(acc => acc.id === transaction.accountId);
  const currency = account?.currency || transaction.currencyId || 'USD';

  // Helper function to detect if transaction is a transfer
  const isTransferTransaction = (transaction: LocalTransaction): boolean => {
    return transaction.type === 'transfer' ||
           transaction.transferDetails !== undefined ||
           (transaction as any).to_account_id !== null && (transaction as any).to_account_id !== undefined ||
           (transaction as any).metadata?.isTransfer === true ||
           (transaction as any).toAccountId !== null && (transaction as any).toAccountId !== undefined;
  };

  // Helper function to get transfer account information
  const getTransferAccountInfo = (transaction: LocalTransaction) => {
    if (!isTransferTransaction(transaction)) return null;

    // Determine direction based on amount or explicit direction
    const direction = transaction.transferDetails?.direction || 
                     (transaction as any).metadata?.direction ||
                     (transaction.amount < 0 ? 'out' : 'in');

    // Get target account ID
    const targetAccountId = transaction.transferDetails?.toAccountId ||
                           transaction.transferDetails?.fromAccountId ||
                           (transaction as any).metadata?.toAccountId ||
                           (transaction as any).metadata?.fromAccountId ||
                           (transaction as any).to_account_id ||
                           (transaction as any).toAccountId;

    // For outgoing transfers, we want the destination account
    // For incoming transfers, we want the source account
    let linkedAccountId: string | undefined;
    
    if (direction === 'out') {
      linkedAccountId = transaction.transferDetails?.toAccountId ||
                       (transaction as any).metadata?.toAccountId ||
                       (transaction as any).to_account_id ||
                       (transaction as any).toAccountId;
    } else {
      linkedAccountId = transaction.transferDetails?.fromAccountId ||
                       (transaction as any).metadata?.fromAccountId ||
                       targetAccountId;
    }

    const linkedAccount = linkedAccountId ? accounts.find(acc => acc.id === linkedAccountId) : null;
    const accountName = linkedAccount?.name || 'Unknown Account';

    return {
      direction,
      accountName,
      linkedAccount
    };
  };

  /**
   * Render content for a specific column
   */
  const renderColumnContent = (columnId: string, transaction: LocalTransaction) => {
    switch(columnId) {
      case 'date':
        return (
          <div className="text-gray-700 dark:text-gray-300">
            {formatTransactionDate(transaction)}
          </div>
        );
        
      case 'description':
        return (
          <div className="font-medium text-gray-700 dark:text-gray-300">
            {transaction.description || transaction.title || '-'}
          </div>
        );
        
      case 'notes':
        return (
          <div className="text-gray-700 dark:text-gray-300">
            {transaction.notes || <span className="text-gray-500 dark:text-gray-400">-</span>}
          </div>
        );
        
      case 'amount':
        return formatAmountWithColor(transaction.amount, currency);
        
      case 'category':
        // Handle transfer transactions specially
        if (isTransferTransaction(transaction)) {
          const transferInfo = getTransferAccountInfo(transaction);
          if (transferInfo) {
            const { direction, accountName } = transferInfo;
            const transferText = direction === 'out' 
              ? `Transfer to ${accountName}`
              : `Transfer from ${accountName}`;
              
            return (
              <span 
                className="category-tag"
                style={{
                  backgroundColor: '#3b82f615', // Blue color for transfers
                  color: '#3b82f6',
                  border: '1px solid #3b82f630'
                }}
              >
                {transferText}
              </span>
            );
          }
        }
        
        // Handle regular category display for non-transfer transactions
        if (transaction.categoryId) {
          // First, check if it's a main category
          const mainCategory = categories.find(c => c.id === transaction.categoryId);
          if (mainCategory) {
            return (
              <span 
                className="category-tag"
                style={{
                  backgroundColor: `${mainCategory.color || '#CBD5E0'}15`,
                  color: mainCategory.color || '#718096',
                  border: `1px solid ${mainCategory.color || '#CBD5E0'}30`
                }}
              >
                {mainCategory.name}
              </span>
            );
          } else {
            // If not a main category, check if it's a subcategory
            for (const main of categories) {
              if (main.subcategories) {
                const sub = main.subcategories.find((s: { id: string }) => s.id === transaction.categoryId);
                if (sub) {
                  return (
                    <span 
                      className="category-tag"
                      style={{
                        backgroundColor: `${sub.color || main.color || '#CBD5E0'}15`,
                        color: sub.color || main.color || '#718096',
                        border: `1px solid ${sub.color || main.color || '#CBD5E0'}30`
                      }}
                    >
                      {main.name} → {sub.name}
                    </span>
                  );
                }
              }
            }
          }
        }
        return <span className="text-gray-500">Uncategorized</span>;
        
      case 'tags':
        return (
          <div className="flex flex-wrap gap-1">
            {Array.isArray(transaction.tags) && transaction.tags.length > 0 ? (
              transaction.tags.map((tagId: string) => {
                const tag = tags.find(t => t.id === tagId);
                
                return (
                  <span 
                    key={tagId}
                    className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium"
                    style={{
                      backgroundColor: `${tag?.color || '#CBD5E0'}15`,
                      color: tag?.color || '#718096',
                      border: `1px solid ${tag?.color || '#CBD5E0'}30`
                    }}
                  >
                    {tag?.name || `Tag ${tagId.substring(0, 4)}...`}
                  </span>
                );
              })
            ) : (
              <span className="text-gray-500 dark:text-gray-400">No tags</span>
            )}
          </div>
        );
        
      case 'account':
        if (transaction.accountId) {
          const accountColor = account?.color || '#CBD5E0';
          return (
            <div className="flex items-center">
              <span 
                className="account-indicator" 
                style={{ backgroundColor: accountColor }}
              ></span>
              <span>{account?.name || 'Unknown Account'}</span>
            </div>
          );
        }
        return <span className="text-gray-500">No Account</span>;
        
      case 'payee':
        // For transfer transactions, don't show payee (keep it null/empty)
        if (isTransferTransaction(transaction)) {
          return (
            <div className="text-gray-700 dark:text-gray-300">
              <span className="text-gray-500 dark:text-gray-400">-</span>
            </div>
          );
        }
        
        // For regular transactions, show payee as usual
        return (
          <div className="text-gray-700 dark:text-gray-300">
            {transaction.payee || <span className="text-gray-500 dark:text-gray-400">-</span>}
          </div>
        );
        
      case 'status':
        const statusColors = {
          completed: 'text-green-600 dark:text-green-400',
          pending: 'text-yellow-600 dark:text-yellow-400',
          cancelled: 'text-red-600 dark:text-red-400'
        };
        return (
          <span className={cn('capitalize', statusColors[transaction.status] || 'text-gray-600')}>
            {transaction.status}
          </span>
        );
        
      default:
        return <span className="text-gray-500">-</span>;
    }
  };

  return (
    <tr
      key={transaction.id}
      className={cn(
        "transaction-row group cursor-pointer",
        isSelected ? 'selected' : ''
      )}
      onClick={(e) => {
        // Don't trigger row click if clicking on checkbox or action menu
        const target = e.target as HTMLElement;
        const isCheckbox = target.closest('.checkbox-column');
        const isActionMenu = target.closest('.action-column');
        
        if (!isCheckbox && !isActionMenu) {
          onEdit(transaction);
        }
      }}
    >
      {/* Checkbox column */}
      <td className="checkbox-column">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelection(transaction.id)}
          aria-label={`Select transaction ${transaction.description}`}
          className={cn(
            "transition-all duration-200 h-4 w-4",
            isSelected 
              ? "opacity-100" 
              : "opacity-0 group-hover:opacity-70 hover:opacity-100",
            "data-[state=checked]:bg-primary-500/20 data-[state=checked]:border-primary-500 data-[state=checked]:text-primary-500 dark:data-[state=checked]:bg-primary-500 dark:data-[state=checked]:text-white",
            "border-gray-300 hover:border-primary-500",
            "rounded"
          )}
        />
      </td>
      
      {/* Dynamic columns */}
      {columnOrder.map((columnId) => {
        const columnVisible = transactionColumns && 
                          transactionColumns[columnId] && 
                          transactionColumns[columnId].visible;
        
        if (!columnVisible) return null;
        
        return (
          <td
            key={columnId}
            className="p-3 text-sm border-b border-gray-100 dark:border-gray-800"
          >
            {renderColumnContent(columnId, transaction)}
          </td>
        );
      })}
      
      {/* Actions column */}
      <td className="action-column">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(transaction)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(transaction.id)}
              className="text-red-600 dark:text-red-400"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}; 